import { Box, Skeleton, Stack, Typography } from '@mui/material'
import {
  TCheckoutOrder,
  TCheckoutOrderDetails,
} from '../../../../../core/types/CheckoutOrder'
import ListItemDetail from '../../../../../components/ListItem/ListItemDetail'
import NumberFunctions from '../../../../../core/functions/NumberFunctions'
import { useContext } from 'react'
import { AuthContext } from '../../../../../core/context/AuthContext'
import { TProductVoucherValue } from '../../../../../core/types/Voucher'
import ListItemDefault from '../../../../../components/ListItem/ListItemDefault'
import CheckoutRecipientTag from '../../../../../components/Checkout/Recipients/CheckoutRecipientTag'

export interface IOrderDetailValuesProp {
  checkoutOrder?: TCheckoutOrder
  details?: TCheckoutOrderDetails
  loading: boolean
}

const OrderDetailValues = ({
  checkoutOrder,
  details,
  loading,
}: IOrderDetailValuesProp) => {
  const { isUserSysAdmin, getTenantId } = useContext(AuthContext)

  const basePrice = details?.basePrice ?? 0
  const voucherValue = details?.voucherValue ?? 0

  const isVoucher =
    details?.voucherIdentifier !== undefined &&
    details?.voucherIdentifier !== null &&
    details?.voucherIdentifier !== ''

  const isVoucherValue =
    isVoucher &&
    details?.voucherType === TProductVoucherValue.value &&
    checkoutOrder?.mainProduct

  const isVoucherPercentage =
    isVoucher && details?.voucherType === TProductVoucherValue.percentage

  const discount = (basePrice * voucherValue) / 100;

  const formatDate = (value?: Date | string | null) => {
    if (!value) return '-'
    const date = value instanceof Date ? value : new Date(value)
    if (Number.isNaN(date.getTime())) return '-'
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date)
  }

  if (loading) {
    return (
      <Stack spacing={2} sx={{ p: 2 }}>
        <Skeleton variant="rounded" width="100%" height="45px" />
        <Skeleton variant="rounded" width="100%" height="45px" />
        <Skeleton variant="rounded" width="100%" height="45px" />
        <Skeleton variant="rounded" width="100%" height="45px" />
        <Skeleton variant="rounded" width="100%" height="45px" />
        <Skeleton variant="rounded" width="100%" height="45px" />
        <Skeleton variant="rounded" width="100%" height="45px" />
        <Skeleton variant="rounded" width="100%" height="45px" />
      </Stack>
    )
  }

  return (
    <Box>
      <ListItemDefault
        title="Preço base do produto"
        value={NumberFunctions.formatMoneyDefault(basePrice)}
      />
      {(details?.quantity ?? 0) > 1 && (
        <ListItemDefault
          title="Quantidade"
          value={NumberFunctions.toNumber(details?.quantity)}
        />
      )}
      {isVoucherValue && (
        <ListItemDefault
          title="Desconto"
          value={NumberFunctions.formatMoneyDefault(voucherValue)}
        />
      )}
      {isVoucherPercentage && (
        <ListItemDefault
          title="Desconto"
          value={NumberFunctions.formatMoneyDefault(discount)}
        />
      )}
      <ListItemDefault
        title="Taxas"
        value={NumberFunctions.formatMoneyDefault(details?.rates)}
      />
      {isUserSysAdmin() && (details?.installmentValues ?? 0) > 0 && (
        <ListItemDefault
          title="Parcelamento"
          value={NumberFunctions.formatMoneyDefault(details?.installmentValues)}
        />
      )}
      {(isUserSysAdmin() || getTenantId() === details?.tenantId) && (
        <ListItemDefault title="" px={0} py={0}>
          <Box width="100%">
            <ListItemDetail
              title="Divisão dos valores"
              value={
                <Stack direction="column" spacing={1}>
                  {details?.recipients
                    ?.sort((a, b) => Number(a.valueType) - Number(b.valueType))
                    .map((recipient, index) => (
                      <Box key={index} sx={{ mb: 0.5 }}>
                        <Typography variant="body2" sx={{ display: 'block' }}>
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            {recipient.name}:
                          </Box>{' '}
                          <Box
                            component="span"
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.75,
                              verticalAlign: 'middle',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {NumberFunctions.formatMoneyDefault(
                              recipient.value
                            )}
                            <CheckoutRecipientTag
                              description={recipient.valueTypeDescription}
                              valueType={recipient.valueType}
                            />
                          </Box>
                        </Typography>
                      </Box>
                    ))}
                </Stack>
              }
              viewDivider={false}
            />
          </Box>
        </ListItemDefault>
      )}
      <Typography variant="subtitle2" sx={{ px: 2, py: 1 }}>
        Seu recebimento
      </Typography>
      <ListItemDefault
        title="Valor"
        value={NumberFunctions.formatMoneyDefault(details?.value)}
      />
            <ListItemDefault
        title="Disponível agora"
        value={
          details?.immediateAvailableAmount == null
            ? '-'
            : NumberFunctions.formatMoneyDefault(details.immediateAvailableAmount)
        }
      />

      <ListItemDefault
        title="A liberar"
        value={
          details?.expectedReleaseAmount == null
            ? '-'
            : NumberFunctions.formatMoneyDefault(details.expectedReleaseAmount)
        }
      />

      <ListItemDefault
        title="Previsão de liberação"
        value={formatDate(details?.expectedReleaseAt)}
      />

    </Box>
  )
}

export default OrderDetailValues
